import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import { WorksheetMaterialCalcMethod } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { useFormContext } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { WorksheetInputField, WorksheetSelectField } from "./WorksheetFormFields";
import {
  CALC_METHOD_OPTIONS,
  COVERAGE_UNIT_OPTIONS,
  OUTPUT_UNIT_OPTIONS,
  THICKNESS_UNIT_OPTIONS,
} from "./worksheetFormValues";

const JOB_UNIT_OPTIONS = [{ value: "", label: "Line unit" }, ...COVERAGE_UNIT_OPTIONS];

type WorksheetMaterialCalcFieldsProps = {
  index: number;
  calcMethod: WorksheetItemInput["costRows"][number]["calcMethod"];
  isReadOnly?: boolean;
  quantityOverridden: boolean;
};

export default function WorksheetMaterialCalcFields({
  index,
  calcMethod,
  isReadOnly,
  quantityOverridden,
}: WorksheetMaterialCalcFieldsProps) {
  const { setValue } = useFormContext<WorksheetItemInput>();

  return (
    <>
      <MDBox
        display="grid"
        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        gap="12px"
        alignItems="start"
      >
        <WorksheetSelectField
          name={`costRows.${index}.calcMethod`}
          label="Quantity calculation"
          options={CALC_METHOD_OPTIONS}
          isReadOnly={isReadOnly}
          onValueChange={() =>
            setValue(`costRows.${index}.quantityOverridden`, false, { shouldDirty: true })
          }
        />
        <WorksheetInputField
          name={`costRows.${index}.jobSizeOverride`}
          label="Job size"
          isReadOnly={isReadOnly}
        />
        {calcMethod !== WorksheetMaterialCalcMethod.MANUAL && (
          <WorksheetSelectField
            name={`costRows.${index}.jobSizeUnit`}
            label="Job size unit"
            options={JOB_UNIT_OPTIONS}
            allowEmpty
            isReadOnly={isReadOnly}
          />
        )}

        {calcMethod === WorksheetMaterialCalcMethod.COVERAGE && (
          <>
            <WorksheetInputField
              name={`costRows.${index}.coverageRate`}
              label="Coverage rate"
              isReadOnly={isReadOnly}
            />
            <WorksheetSelectField
              name={`costRows.${index}.coverageUnit`}
              label="Coverage unit"
              options={COVERAGE_UNIT_OPTIONS}
              allowEmpty
              isReadOnly={isReadOnly}
            />
          </>
        )}

        {calcMethod === WorksheetMaterialCalcMethod.DENSITY && (
          <>
            <WorksheetInputField
              name={`costRows.${index}.thickness`}
              label="Thickness (inches)"
              isReadOnly={isReadOnly}
            />
            <WorksheetSelectField
              name={`costRows.${index}.thicknessUnit`}
              label="Thickness unit"
              options={THICKNESS_UNIT_OPTIONS}
              allowEmpty
              isReadOnly={isReadOnly}
            />
            <WorksheetInputField
              name={`costRows.${index}.density`}
              label="Density (lb/ft3)"
              isReadOnly={isReadOnly}
            />
            <WorksheetSelectField
              name={`costRows.${index}.outputUnit`}
              label="Output unit"
              options={OUTPUT_UNIT_OPTIONS}
              isReadOnly={isReadOnly}
            />
          </>
        )}

        {calcMethod === WorksheetMaterialCalcMethod.THICKNESS_FACTOR && (
          <>
            <WorksheetInputField
              name={`costRows.${index}.weightFactor`}
              label="Weight factor"
              isReadOnly={isReadOnly}
            />
            <WorksheetSelectField
              name={`costRows.${index}.outputUnit`}
              label="Output unit"
              options={OUTPUT_UNIT_OPTIONS}
              isReadOnly={isReadOnly}
            />
          </>
        )}
      </MDBox>

      {quantityOverridden && !isReadOnly && (
        <MDBox display="flex" justifyContent="flex-end">
          <UiTypography
            uiVariant="body-sm"
            sx={{ color: draftColors.blue, cursor: "pointer", fontSize: "10px" }}
            onClick={() =>
              setValue(`costRows.${index}.quantityOverridden`, false, { shouldDirty: true })
            }
          >
            Restore calculation
          </UiTypography>
        </MDBox>
      )}
    </>
  );
}
